import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Facebook,
  Twitter,
  Linkedin,
  Github,
  Instagram,
  MapPin,
  Mail,
  Phone,
} from "lucide-react";
import Logo from "assets/images/logos/Logo.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTiktok } from "@fortawesome/free-brands-svg-icons";

const Links = [
  {
    icon: Facebook,
    url: "https://www.facebook.com/profile.php?id=61583907059473",
  },
  { icon: Instagram, url: "https://www.instagram.com/thedevhouseco/" },
  {
    icon: Linkedin,
    url: "https://www.linkedin.com/company/thedevhouse1",
  },
  {
    icon: "tiktok",
    url: "https://www.tiktok.com/@thedevhouse?_r=1&_t=ZS-93aVUqO32H3",
  },
];

function Footer() {
  return (
    <Box
      component="footer"
      sx={{
        background: "linear-gradient(135deg, #0A0E1C, #1e293b)",
        color: "white",
        pt: 6,
        pb: 3,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Brand + Description */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Box
                component="img"
                src={Logo}
                alt="logo"
                height="55px"
                sx={{ filter: "brightness(0) invert(1)", mr: 1 }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontSize: "24px",
                  fontWeight: 400,
                  paddingTop: "5px",
                  background: "#ffff",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                The DevHouse
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
              We build tailored solutions to help your business grow. Trusted by
              startups and enterprises alike.
            </Typography>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 2,
                fontSize: "1rem",
                color: "#0A8EF2", // accent cyan
              }}
            >
              Quick Links
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {[
                { name: "Home", link: "/" },
                { name: "Consultation", link: "/ConsultationProgram" },
                { name: "Careers", link: "/Careers" },
                { name: "Past Projects", link: "/PastProjects" },
              ].map((item) => (
                <Typography
                  key={item.name}
                  component="a"
                  href={item.link}
                  sx={{
                    textDecoration: "none",
                    color: "rgba(255,255,255,0.7)",
                    "&:hover": { color: "#22d3ee" },
                    fontSize: "0.95rem",
                  }}
                >
                  {item.name}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={6} sm={4} md={3}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 2,
                fontSize: "1rem",
                color: "#0A8EF2", // accent cyan
              }}
            >
              Contact
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
              <MapPin size={18} color="#ffff" />
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                <a
                  href="https://www.google.com/maps/place/25+El-Montaza+St,+Al+Matar,+El+Nozha,+Cairo+Governorate+4470320/@30.1101378,31.3363123,17z/data=!3m1!4b1!4m6!3m5!1s0x145815c2763d9077:0xa6ee094909dad2e!8m2!3d30.1101378!4d31.3388872!16s%2Fg%2F11h3jh2g31?entry=ttu&g_ep=EgoyMDI2MDExMy4wIKXMDSoASAFQAw%3D%3D"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#ffff" }}
                >
                  Heliopolis, Cairo, Egypt
                </a>
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
              <Mail size={18} color="#ffff" />
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                <a
                  href="mailto:sales@thedevhouse.io"
                  style={{ color: "#fff", textDecoration: "none" }}
                >
                  sales@thedevhouse.io
                </a>
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Phone size={18} color="#ffff" />
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                <a
                  href="tel:+201036689217"
                  style={{ color: "#fff", textDecoration: "none" }}
                >
                  +20 103 668 9217
                </a>
              </Typography>
            </Box>
          </Grid>
          {/* Social Links */}
          <Grid item xs={12} sm={4} md={3}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 2,
                fontSize: "1rem",
                color: "#0A8EF2", // accent cyan
              }}
            >
              Follow Us
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {Links.map(({ icon, url }, index) => (
                <IconButton
                  key={index}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.2)",
                    "&:hover": {
                      background: "rgba(34,211,238,0.15)",
                      borderColor: "#22d3ee",
                    },
                  }}
                >
                  {icon === "tiktok" ? (
                    <FontAwesomeIcon icon={faTiktok} size="xs" />
                  ) : (
                    (() => {
                      const Icon = icon;
                      return <Icon size={20} />;
                    })()
                  )}
                </IconButton>
              ))}
            </Box>
          </Grid>
        </Grid>

        {/* Divider */}
        <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,0.2)" }} />

        {/* Bottom Bar */}
        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            color: "rgba(255,255,255,0.6)",
            fontSize: "0.85rem",
          }}
        >
          © {new Date().getFullYear()} The DevHouse. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
}

export default Footer;
